import React, { useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import gsap from "gsap";
import classes from "./Modal.module.css";
import close from "../../assets/close.svg";

/**
 * Article modal shared by the Impact cards and the Events/News cards, which
 * previously each carried their own copy of this markup and both reached into
 * Impact.module.css for the styling.
 *
 * Closes on backdrop click, on the close button, and on Escape; locks the page
 * behind it while open.
 *
 * Rendered through a portal into <body>. `position: fixed` is resolved against
 * the nearest ancestor carrying a transform, filter or containment - and the
 * Impact cards apply `transform: translateY(-6px)` on hover, which is exactly
 * the state the card is in at the moment you click it. The dialog was being
 * laid out inside the card and clipped by its `overflow: hidden`. A portal
 * takes it out of that subtree entirely, so no ancestor's styles can trap it.
 */
export default function Modal({ title, image, children, onClose }) {
  const panelRef = useRef(null);
  const backdropRef = useRef(null);

  // Escape to close.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // Stop the page behind the modal from scrolling, and compensate for the
  // scrollbar going away so the layout doesn't jump.
  useEffect(() => {
    const { body, documentElement } = document;
    const previousOverflow = body.style.overflow;
    const previousPadding = body.style.paddingRight;
    const scrollbar = window.innerWidth - documentElement.clientWidth;

    body.style.overflow = "hidden";
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;

    return () => {
      body.style.overflow = previousOverflow;
      body.style.paddingRight = previousPadding;
    };
  }, []);

  useLayoutEffect(() => {
    const reduced =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return undefined;

    const ctx = gsap.context(() => {
      gsap.from(backdropRef.current, { opacity: 0, duration: 0.25, ease: "power2.out" });
      gsap.from(panelRef.current, {
        opacity: 0,
        y: 24,
        scale: 0.97,
        duration: 0.4,
        ease: "power3.out",
      });
    });
    return () => ctx.revert();
  }, []);

  return createPortal(
    <div
      className={classes.backdrop}
      ref={backdropRef}
      onClick={onClose}
      role="presentation"
    >
      <div
        className={classes.panel}
        ref={panelRef}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <button type="button" className={classes.close} onClick={onClose} aria-label="Close">
          <img src={close} alt="" width="16" height="16" />
        </button>

        <h2 className={classes.title}>{title}</h2>

        {image && (
          <div className={classes.media}>
            <img src={image} alt="" className={classes.image} />
          </div>
        )}

        <div className={classes.body}>{children}</div>
      </div>
    </div>,
    document.body
  );
}
